import { IMAGES_BASE_URL } from '@/lib/config';
import { capitalizeCityName, getLocale } from '@/lib/utils';
import { api } from '@/utils/api';
import { mediumImpact } from '@/utils/capacitorUtils';
import { supabase } from '@/utils/supabase';
import {
  IonActionSheet,
  IonButton, IonButtons,
  IonContent, IonDatetime, IonFooter,
  IonHeader, IonIcon,
  IonItem,
  IonLoading,
  IonModal,
  IonPage,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar
} from '@ionic/react';
import { arrowBackOutline } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GameFormat, GameSetup } from './types';

interface GameFormComponentProps {
  /**
   * Initial data for the form. For new games, this will contain club/course info.
   * For existing games, this will be the current game data.
   */
  initialData: Partial<GameSetup>;

  /**
   * Optional game ID. If provided, the component will function in "edit mode".
   * If not provided, it will function in "create mode".
   */
  gameId?: string;

  /**
   * Required for create mode (GameSetupForm). This is called when the user clicks "Next".
   */
  onNext?: (data: Partial<GameSetup>) => boolean | Promise<boolean>;

  /**
   * Optional back handler for both modes.
   */
  onBack?: () => void;

  /**
   * Alternative back handler for GameSetupForm
   */
  backHandler?: () => boolean | Promise<boolean>;

  /**
   * Required for create mode (GameSetupForm)
   */
  nav?: HTMLIonNavElement;

  /**
   * Optional callback for when update is successful in edit mode (GameSettingsEditor)
   */
  onUpdateSuccess?: () => void;
}

const GameFormComponent: React.FC<GameFormComponentProps> = ({
  initialData,
  gameId,
  onNext,
  onBack,
  backHandler,
  nav,
  onUpdateSuccess
}) => {
  const { t } = useTranslation();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showHolesPicker, setShowHolesPicker] = useState(false);
  const datetime = useRef<null | HTMLIonDatetimeElement>(null);

  // For edit mode (GameSettingsEditor)
  const [isLoading, setIsLoading] = useState(initialData ? false : true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [gameData, setGameData] = useState<Partial<GameSetup>>({
    name: initialData.name || '',
    date: initialData.date || new Date().toISOString(),
    holes: initialData.course?.holes || 18,
    startingHole: initialData.startingHole || 1,
    format: initialData.format || 'strokeplay',
    scoringMode: initialData.scoringMode || 'gross',
    club: initialData.club,
    course: initialData.course,
    isPrivate: initialData.isPrivate !== undefined ? initialData.isPrivate : false,
    countForHandicap: initialData.countForHandicap !== undefined ? initialData.countForHandicap : false,
    game: initialData.game,
  });

  // Load game data if in edit mode
  useEffect(() => {
    if (gameId) {
      loadGameData();
    }
  }, [gameId]);

  // Load game data from API for edit mode
  const loadGameData = async () => {
    if (!gameId || !initialData) return;

    try {
      setIsLoading(true);
      const response = await api.get(`/game/${gameId}`);
      const game = response.data;

      // Map API response to our game data structure
      setGameData({
        name: game.name || '',
        date: game.date || new Date().toISOString(),
        holes: game.holes || 18,
        startingHole: game.startingHole || 1,
        format: game.format || 'strokeplay',
        scoringMode: game.scoringMode || 'gross',
        isPrivate: game.isPrivate !== undefined ? game.isPrivate : false,
        countForHandicap: game.countForHandicap !== undefined ? game.countForHandicap : false,
        game: game.game || game,
      });
    } catch (error) {
      console.error('Failed to load game data:', error);
      setToastMessage(t('error_loading_game'));
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission based on mode
  const handleSubmit = async () => {
    if (gameId) {
      // Edit mode - update existing game
      await saveGameSettings();
    } else if (onNext) {
      // Create mode - go to next step
      onNext(gameData);
    }
  };

  // Save game settings for edit mode
  const saveGameSettings = async () => {
    if (!gameId) return;

    try {
      // Update game in the database
      await api.put(`/game/${gameId}`, gameData);

      // Notify other players about the update
      await supabase
        .from('game_settings_updates')
        .insert({
          game_id: gameId,
          updated_at: new Date().toISOString()
        });

      setToastMessage(t('game_settings_updated'));
      setShowToast(true);

      // Call the onUpdateSuccess callback if provided
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
    } catch (error) {
      console.error('Failed to update game settings:', error);
      setToastMessage(t('error_updating_game'));
      setShowToast(true);
    }
  };

  // Navigate to the format picker using IonNav (create mode)
  const navigateToFormatPicker = () => {
    if (!nav) return;

    import('./GameFormatPicker').then(module => {
      const GameFormatPicker = module.default;
      nav.push((props: any) => (
        <GameFormatPicker
          {...props}
          nav={nav}
          selectedFormat={gameData.format}
          onSelectFormat={(format: GameFormat) => {
            setGameData({ ...gameData, format });
          }}
        />
      ));
    });
  };

  // Handle format selection directly (edit mode)
  const handleFormatChange = () => {
    if (nav) {
      // Create mode - navigate to picker
      navigateToFormatPicker();
    } else {
      // Edit mode - cycle through formats
      const formats = ['strokeplay', 'stableford', 'matchplay'];
      const currentIndex = formats.indexOf(gameData.format as string);
      const nextIndex = (currentIndex + 1) % formats.length;
      setGameData({ ...gameData, format: formats[nextIndex] as GameFormat });
      mediumImpact();
    }
  };

  const isEditMode = !!gameId;

  return (
    <IonPage>
      <IonHeader color="dark" className="ion-no-border">
        <IonToolbar color={isEditMode ? "secondary" : "light"}>
          <IonButtons slot="start">
            <IonButton onClick={() => {
              if (backHandler) {
                backHandler();
              } else if (onBack) {
                onBack();
              } else if (nav) {
                nav.pop();
              }
            }}>
              <IonIcon slot="icon-only" icon={arrowBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle>{t('game_settings')}</IonTitle>
          <IonButtons slot="end">
            {isEditMode ? (
              <IonButton color="primary" onClick={handleSubmit}>{t('save')}</IonButton>
            ) : (
              <IonButton color="primary" onClick={handleSubmit}>{t('next')}</IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent color="light" scroll-y="false">
        {/* Loading indicator (edit mode) */}
        <IonLoading isOpen={isLoading} message={t('loading')} />

        {/* Toast for notifications (edit mode) */}
        <IonToast
          isOpen={showToast}
          message={toastMessage}
          duration={2000}
          position="top"
        />

        {!isLoading && (
          <div className='max-w-3xl mx-auto'>
            {/* Card: Course, Time, Holes */}
            <div className="bg-card rounded-2xl p-4 m-2 space-y-3">
              {/* Display course info based on data source */}
              {isEditMode && gameData.game ? (
                <div className="flex items-center mb-2">
                  <img
                    src={`${IMAGES_BASE_URL}/${gameData.game.clubId}.jpg`}
                    alt={gameData.game.clubName}
                    className="size-12 rounded-md object-cover mr-3"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/assets/images/course_fallback.webp';
                    }}
                  />
                  <div>
                    <div className="font-semibold text-base">{gameData.game.clubName}</div>
                    <div className="text-sm text-gray-500">
                      {gameData.game.courseName || '--'} {gameData.holes || '--'} {t('holes')} - Par: {gameData.game.par || '--'}
                    </div>
                  </div>
                </div>
              ) : gameData.club ? (
                <div className="flex items-center mb-2">
                  <img
                    src={`${IMAGES_BASE_URL}/${gameData.club.id}.jpg`}
                    alt={gameData.club.name}
                    className="size-12 rounded-md object-cover mr-3"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/assets/images/course_fallback.webp';
                    }}
                  />
                  <div>
                    <div className="font-semibold text-base">{capitalizeCityName(gameData.club.name)}</div>
                    <div className="text-sm text-gray-500">
                      {gameData.course?.courseName || '--'} {gameData.course?.holes || '--'} {t('holes')} - Par: {gameData.course?.par || '--'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center p-4 border border-dashed rounded-lg">
                  <div className="text-center text-muted-foreground">
                    <p>{t('No course selected')}</p>
                    <p className="text-sm mt-1">{t('Please go back and select a course')}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-3 mt-2">
                <span className="font-semibold">{t('date')}</span>
                <div className="flex items-center">
                  <div className="text-right" onClick={() => setShowDatePicker(true)}>
                    <div className="font-medium">{gameData.date
                      ? new Date(gameData.date).toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : '--/--/----'}
                    </div>
                    <div className="text-xs text-muted-foreground">{gameData.date
                      ? new Date(gameData.date).toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' })
                      : ''}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-semibold">{t('holes')}</span>
                <button
                  onClick={() => {
                    const totalHoles = isEditMode
                      ? (gameData.game?.holes || 18)
                      : (gameData.course?.holes || 18);

                    if (totalHoles > 9) {
                      setShowHolesPicker(true);
                    }
                  }}
                  className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <span className="font-semibold lowercase">
                    {gameData.holes === 18 && `18 ${t('holes')}`}
                    {gameData.holes === 9 && `9 ${t('holes')}`}
                    {gameData.holes === 0 && `9 ${t('holes')} (Aller)`}
                  </span>
                </button>
              </div>
            </div>

            {/* Game Format & Scoring Section */}
            <div>
              <div className="ml-4 p-2 pb-0 text-xs font-semibold text-gray-400 uppercase mb-2">{t('game')}</div>
              <div className="bg-card rounded-2xl px-4 py-3 m-2 space-y-4">
                {/* Game Format with Info Icon */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <span className="font-semibold">{t('game_format')}</span>
                    <IonIcon icon="information-circle-outline" className="ml-1 text-primary text-sm" />
                    {!isEditMode && (
                      <IonModal trigger="format-info" className="auto-size" keepContentsMounted={true}>
                        <IonHeader>
                          <IonToolbar>
                            <IonTitle>{t('game_format_info')}</IonTitle>
                            <IonButtons slot="end">
                              <IonButton onClick={() => (document.querySelector('ion-modal[trigger="format-info"]') as HTMLIonModalElement)?.dismiss()}>
                                {t('close')}
                              </IonButton>
                            </IonButtons>
                          </IonToolbar>
                        </IonHeader>
                        <IonContent className="ion-padding">
                          <h4 className="font-medium mb-2">{t('Stroke Play')}</h4>
                          <p className="text-sm mb-4">{t('format_stroke_play_explanation')}</p>

                          <h4 className="font-medium mb-2">{t('Stableford')}</h4>
                          <p className="text-sm mb-4">{t('format_stableford_explanation')}</p>

                          <h4 className="font-medium mb-2">{t('Match Play')}</h4>
                          <p className="text-sm">{t('format_match_play_explanation')}</p>
                        </IonContent>
                      </IonModal>
                    )}
                  </div>
                  <button
                    onClick={handleFormatChange}
                    className="font-medium px-3 py-1.5 rounded-lg bg-primary/10 text-primary"
                  >
                    {gameData.format === 'strokeplay' && t('Stroke Play')}
                    {gameData.format === 'stableford' && t('Stableford')}
                    {gameData.format === 'matchplay' && t('Match Play')}
                    {(gameData.format as string) === 'skins' && t('Skins')}
                    {(gameData.format as string) === 'bestball' && t('Best Ball')}
                    {(gameData.format as string) === 'scramble' && t('Scramble')}
                    {(gameData.format as string) === 'alternate' && t('Alternate Shot')}
                    {!['strokeplay', 'stableford', 'matchplay', 'skins', 'bestball', 'scramble', 'alternate'].includes(gameData.format as string) && t('Free play')}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {gameData.format === 'strokeplay' && t('GameFormats:format_stroke_play_brief')}
                  {gameData.format === 'stableford' && t('GameFormats:format_stableford_brief')}
                  {gameData.format === 'matchplay' && t('GameFormats:format_match_play_brief')}
                </p>

                {/* Scoring Mode with Info - Only show for compatible formats */}
                {(gameData.format === 'strokeplay') && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="font-semibold">{t('Scoring Mode')}</span>
                        <IonIcon icon="information-circle-outline" className="ml-1 text-primary text-sm" />
                        {!isEditMode && (
                          <IonModal trigger="scoring-info" className="auto-size" keepContentsMounted={true}>
                            <IonHeader>
                              <IonToolbar>
                                <IonTitle>{t('scoring_mode_info')}</IonTitle>
                                <IonButtons slot="end">
                                  <IonButton onClick={() => (document.querySelector('ion-modal[trigger="scoring-info"]') as HTMLIonModalElement)?.dismiss()}>
                                    {t('close')}
                                  </IonButton>
                                </IonButtons>
                              </IonToolbar>
                            </IonHeader>
                            <IonContent className="ion-padding">
                              <h4 className="font-medium mb-2">{t('Gross Score')}</h4>
                              {/* <p className="text-sm mb-4">{t('scoring_gross_explanation')}</p> */}

                              <h4 className="font-medium mb-2">{t('Net Score')}</h4>
                              {/* <p className="text-sm mb-4">{t('scoring_net_explanation')}</p> */}

                              <div className="bg-primary/10 p-3 rounded-lg mt-4">
                                <h4 className="font-medium mb-1">{t('tip')}</h4>
                                <p className="text-xs">{t('scoring_tip')}</p>
                              </div>
                            </IonContent>
                          </IonModal>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setGameData({
                            ...gameData,
                            scoringMode: gameData.scoringMode === 'gross' ? 'net' : 'gross'
                          });
                          mediumImpact();
                        }}
                        className="font-medium px-3 py-1.5 rounded-lg bg-primary/10 text-primary"
                      >
                        {(!gameData.scoringMode || gameData.scoringMode === 'gross') && t('Gross Score')}
                        {gameData.scoringMode === 'net' && t('Net Score')}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {(!gameData.scoringMode || gameData.scoringMode === 'gross')
                        ? t('GameFormats:scoring_gross_brief')
                        : t('GameFormats:scoring_net_brief')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Handicap */}
            {!isEditMode && (
              <div className='hidden'>
                <div className="ml-4 p-2 pb-0 text-xs font-semibold text-gray-400 uppercase mb-2">{t('handicap')}</div>
                <div className="bg-card rounded-2xl px-4 py-3 mx-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="font-semibold">{t('calculate_handicap')}</span>
                      <IonItem lines="none" className="p-0 m-0 inline-block" button detail={false}>
                        <IonIcon icon="information-circle-outline" className="ml-1 text-primary text-sm" />
                        <IonModal trigger="handicap-info" className="auto-size" keepContentsMounted={true}>
                          <IonHeader>
                            <IonToolbar>
                              <IonTitle>{t('handicap_info')}</IonTitle>
                              <IonButtons slot="end">
                                <IonButton onClick={() => (document.querySelector('ion-modal[trigger="handicap-info"]') as HTMLIonModalElement)?.dismiss()}>
                                  {t('close')}
                                </IonButton>
                              </IonButtons>
                            </IonToolbar>
                          </IonHeader>
                          <IonContent className="ion-padding">
                            <h4 className="font-medium mb-2">{t('what_is_handicap')}</h4>
                            <p className="text-sm mb-4">{t('handicap_explanation')}</p>

                            <h4 className="font-medium mb-2">{t('when_to_count')}</h4>
                            <p className="text-sm mb-4">{t('handicap_count_explanation')}</p>

                            <div className="bg-primary/10 p-3 rounded-lg mt-4">
                              <h4 className="font-medium mb-1">{t('tip')}</h4>
                              <p className="text-xs">{t('handicap_tip')}</p>
                            </div>
                          </IonContent>
                        </IonModal>
                      </IonItem>
                    </div>
                    <IonToggle
                      justify="space-between"
                      className="flex flex-col"
                      checked={gameData.countForHandicap}
                      onIonChange={(e) => { mediumImpact(); setGameData({ ...gameData, countForHandicap: e.detail.checked }) }}
                    ></IonToggle>
                  </div>
                </div>
                <div className="px-4 py-2">
                  <p className="text-xs text-muted-foreground">
                    {gameData.countForHandicap ?
                      <span className="flex items-center">
                        <IonIcon icon="checkmark-circle" className="text-success mr-1 text-sm" />
                        {t('will_count_handicap')}
                      </span> :
                      <span className="flex items-center">
                        <IonIcon icon="information-circle" className="text-muted-foreground mr-1 text-sm" />
                        {t('wont_count_handicap')}
                      </span>
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Visibility */}
            <div>
              <div className="bg-card rounded-2xl px-4 py-3 m-2">
                <IonToggle
                  checked={gameData.isPrivate}
                  onIonChange={e => {
                    mediumImpact();
                    setGameData({ ...gameData, isPrivate: e.detail.checked })
                  }}
                  justify="space-between"
                  className="font-semibold"
                >
                  <div className="flex flex-col font-semibold">
                    <span>{t('private_game')}</span>
                  </div>
                </IonToggle>
              </div>
            </div>
          </div>
        )}
      </IonContent>

      {/* Footer button only shown in create mode */}
      {!isEditMode && (
        <IonFooter className="ion-no-border md:hidden">
          <IonToolbar color="light">
            <IonButton
              shape="round"
              onClick={handleSubmit}
              disabled={!gameData.course}
              className="w-full text-lg transition-colors"
            >
              <div className="flex flex-col">
                {t('add_players')}
              </div>
            </IonButton>
          </IonToolbar>
        </IonFooter>
      )}

      {/* Date Picker Modal */}
      <IonModal
        isOpen={showDatePicker}
        onDidDismiss={() => setShowDatePicker(false)}
        backdropDismiss={true}
        className="auto-size"
        keepContentsMounted={true}
      >
        <IonDatetime
          ref={datetime}
          value={gameData.date}
          locale="fr-FR"
          className='min-h-[400px] min-w-[400px]'
          firstDayOfWeek={1}
          min={new Date(new Date().setDate(new Date().getDate())).toISOString().split('T')[0] + 'T00:00:00'}
          showDefaultButtons={true}
          doneText={t('done')}
          cancelText={t('cancel')}
          onIonChange={(e) => {
            if (e.detail.value) {
              setGameData((prev) => ({
                ...prev,
                date: e.detail.value as string,
              }));
            }
          }}
          onIonCancel={() => {
            setShowDatePicker(false);
          }}
        >
          <span slot="time-label">{t('hour')}</span>
        </IonDatetime>
        <IonToolbar color="light" className='m-0 pb-4'>
          <div className="flex items-center justify-center my-2">
            <div className="h-px bg-gray-300 flex-grow"></div>
            <span className="px-3 text-sm text-muted-foreground">{t('or')}</span>
            <div className="h-px bg-gray-300 flex-grow"></div>
          </div>
          <IonButton
            expand="block"
            fill="solid"
            onClick={() => {
              // Set current date and time
              const now = new Date().toISOString();
              setGameData(prev => ({
                ...prev,
                date: now
              }));
              setShowDatePicker(false);
            }}
          >
            {t('play_now')}
          </IonButton>
        </IonToolbar>
      </IonModal>

      {/* Holes Picker */}
      <IonActionSheet
        isOpen={showHolesPicker}
        onDidDismiss={() => setShowHolesPicker(false)}
        header={t('number_of_holes')}
        buttons={[
          {
            text: `18 ${t('holes')}`,
            handler: () => {
              setGameData({ ...gameData, holes: 18 });
            }
          },
          {
            text: `9 ${t('holes')} (Aller)`,
            handler: () => {
              setGameData({ ...gameData, holes: 0 });
            }
          },
          {
            text: `9 ${t('holes')} (Retour)`,
            handler: () => {
              setGameData({ ...gameData, holes: 9 });
            }
          },
          {
            text: t('cancel'),
            role: 'cancel'
          }
        ]}
      />
    </IonPage>
  );
};

export default GameFormComponent;
